import { useState } from 'react';
import { Alert, Modal, Pressable, Text, TextInput, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Ctx } from './tipos';
import { din, moedaDo, num } from './util';
import { mudarCampos } from '../store/modulos';
import { lerValor } from '../lib/moeda';
import { uid } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { C, alpha } from '../components/theme';

// Orçamento da viagem (0.59, pedido da Ana Beatriz): um orçamento geral da viagem e um para cada parada,
// com categorias sugeridas (Hospedagem, Alimentação com as refeições, Transporte...) que dá para tirar,
// mudar e completar. Ao lado de cada uma aparece quanto já foi gasto (aba Gastos).

// texto 19 (C): dias e porDia = o ⊕ (Dias × Valor por dia); valor guarda o total calculado (as somas continuam iguais)
// texto 23 (E4): em Eventos o ⊕ é "por convidado": qtdConv (quantidade por convidado, ex.: 1 ou 0,1 kg) × unit (valor unitário);
// o total muda sozinho quando muda o número de convidados (não fica guardado). un = unidade da quantidade (kg, L...)
export type PorConv = { qtdConv?: number; unit?: number; un?: string };
export type LinhaOrc = { id: string; nome: string; valor?: number; dias?: number; porDia?: number } & PorConv;
export type CatOrc = { id: string; nome: string; icone: string; valor?: number; linhas?: LinhaOrc[]; dias?: number; porDia?: number } & PorConv;
/** Valor previsto de uma linha (ou categoria sem itens). Com nConv (Eventos), o ⊕ por convidado é calculado na hora. */
export const valorDe = (x: { valor?: number } & PorConv, nConv?: number | null) =>
  nConv != null && x.qtdConv ? (x.unit ? Math.round(x.qtdConv * nConv * x.unit * 100) / 100 : 0) : num(x.valor);

/** Categorias sugeridas. Os nomes ficam em português (são a chave) e aparecem traduzidos. */
export const ORC_PADRAO = (): CatOrc[] => [
  { id: 'hosp', nome: N_('Hospedagem'), icone: '🏨' },
  { id: 'alim', nome: N_('Alimentação'), icone: '🍽️', linhas: [
    { id: 'alim-cafe', nome: N_('Café da manhã') }, { id: 'alim-almoco', nome: N_('Almoço') }, { id: 'alim-tarde', nome: N_('Café da tarde') },
    { id: 'alim-jantar', nome: N_('Jantar') }, { id: 'alim-outros', nome: N_('Outros / Lanches') }] },
  { id: 'transp', nome: N_('Transporte'), icone: '🚗' },
  { id: 'passeios', nome: N_('Passeios'), icone: '🎟️' },
  { id: 'compras', nome: N_('Compras'), icone: '🛍️' },
  { id: 'outro', nome: N_('Outro'), icone: '💡' },
];

export const orcDe = (r: Reg | null | undefined): CatOrc[] => (Array.isArray(r?.c.orc) ? r!.c.orc : ORC_PADRAO());
export const previstoCat = (c: CatOrc, nConv?: number | null) => (c.linhas?.length ? c.linhas.reduce((s, l) => s + valorDe(l, nConv), 0) : valorDe(c, nConv));
export const previstoTotal = (orc: CatOrc[], nConv?: number | null) => orc.reduce((s, c) => s + previstoCat(c, nConv), 0);
export const mesmo = (a: unknown, b: string) => String(a ?? '').trim().toLowerCase() === b.trim().toLowerCase();
// texto 19 (D7): cada gasto tem a sua moeda e nada é convertido. Com `moeda`, as contas do orçamento (que é na moeda do app)
// usam só os gastos nessa moeda; os outros aparecem à parte ("+ € 100,00").
const naMoeda = (g: Reg, moeda?: string) => !moeda || moedaDo(g.c, 'valor', moeda) === moeda;
export const gastoCat = (gastos: Reg[], c: CatOrc, moeda?: string) => gastos.filter(g => !g.c.acerto && naMoeda(g, moeda) && mesmo(g.c.cat, c.nome)).reduce((s, g) => s + num(g.c.valor), 0);
export const gastoLinha = (gastos: Reg[], c: CatOrc, l: LinhaOrc, moeda?: string) => gastos.filter(g => !g.c.acerto && naMoeda(g, moeda) && mesmo(g.c.cat, c.nome) && mesmo(g.c.item, l.nome)).reduce((s, g) => s + num(g.c.valor), 0);
// 0.63: pagamento de acerto entre os viajantes não é gasto da viagem (só zera quem deve a quem)
export const somaGastos = (gastos: Reg[], moeda?: string) => gastos.filter(g => !g.c.acerto && naMoeda(g, moeda)).reduce((s, g) => s + num(g.c.valor), 0);

/** Barra de previsto x gasto: verde até 85%, amarela até 100%, vermelha acima. */
export function Barra({ gasto, previsto }: { gasto: number; previsto: number }) {
  if (previsto <= 0) return null;
  const p = gasto / previsto;
  const cor = p > 1 ? C.danger : p > 0.85 ? C.warn : C.ok;
  return (
    <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2, overflow: 'hidden' }}>
      <View style={{ width: `${Math.min(100, Math.round(p * 100))}%`, height: 6, borderRadius: 3, backgroundColor: cor }} />
    </View>
  );
}

/** Resumo de uma linha: "Gasto R$ 120 de R$ 300" (ou "acima R$ 20"). */
function linhaGasto(gasto: number, previsto: number, moeda: string) {
  if (!previsto && !gasto) return '';
  if (!previsto) return t('Gasto {v}', { v: din(gasto, moeda) });
  if (gasto > previsto) return t('Gasto {g} de {p} · {a} acima', { g: din(gasto, moeda), p: din(previsto, moeda), a: din(gasto - previsto, moeda) });
  return t('Gasto {g} de {p}', { g: din(gasto, moeda), p: din(previsto, moeda) });
}

function Valor({ valor, pode, onSalvar, rotulo }: { valor?: number; pode: boolean; onSalvar: (v: number | undefined) => void; rotulo: string }) {
  const inicial = valor ? String(valor).replace('.', ',') : '';
  const [txt, setTxt] = useState(inicial);
  const [foco, setFoco] = useState(false);
  const mostrado = foco ? txt : inicial;
  const salvar = () => { setFoco(false); const v = lerValor(txt); const n = isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : undefined; if (n !== valor) onSalvar(n); };
  return (
    <TextInput editable={pode} value={mostrado} onFocus={() => { setTxt(inicial); setFoco(true); }} onChangeText={setTxt} onSubmitEditing={salvar} onBlur={salvar}
      keyboardType="decimal-pad" returnKeyType="done" inputAccessoryViewButtonLabel={t('Concluído')} placeholder="0,00" placeholderTextColor={C.mute} accessibilityLabel={rotulo}
      style={{ width: 104, backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7, color: C.text, fontSize: 15, fontWeight: '700', textAlign: 'right' }} />
  );
}

function NovoNome({ ph, cor, onOk }: { ph: string; cor: string; onOk: (nome: string) => void }) {
  const [v, setV] = useState('');
  const ok = () => { const n = v.trim(); if (!n) return; onOk(n.slice(0, 40)); setV(''); };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <TextInput value={v} onChangeText={setV} onSubmitEditing={ok} returnKeyType="done" placeholder={ph} placeholderTextColor={C.mute} accessibilityLabel={ph}
        style={{ flex: 1, minWidth: 0, backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7, color: C.text, fontSize: 14 }} />
      <Pressable disabled={!v.trim()} onPress={ok} accessibilityLabel={t('Adicionar')}
        style={{ width: 38, height: 34, borderRadius: 10, backgroundColor: v.trim() ? cor : C.card2, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: v.trim() ? '#fff' : C.mute, fontWeight: '900', fontSize: 16 }}>＋</Text>
      </Pressable>
    </View>
  );
}

const Lixeira = ({ onPress, rotulo }: { onPress: () => void; rotulo: string }) => (
  <Pressable onPress={onPress} hitSlop={8} accessibilityLabel={rotulo} style={{ width: 30, alignItems: 'center' }}>
    <Text style={{ fontSize: 15, color: C.sub }}>🗑</Text>
  </Pressable>
);

/** Texto 19 (C2): janelinha do ⊕ com "Dias" e "Valor por dia" (OK; Limpar volta ao valor digitado direto). */
function JanelaDias({ nome, dias, porDia, diasPadrao, moeda, cor, onOk, onLimpar, onFechar }: { nome: string; dias?: number; porDia?: number; diasPadrao: number | null;
  moeda: string; cor: string; onOk: (dias: number, porDia: number) => void; onLimpar?: () => void; onFechar: () => void }) {
  const [d, setD] = useState(dias ? String(dias) : diasPadrao ? String(diasPadrao) : '');
  const [v, setV] = useState(porDia ? String(porDia).replace('.', ',') : '');
  const nd = Math.round(num(d.replace(',', '.'))), nv = lerValor(v);
  const ok = nd > 0 && isFinite(nv) && nv > 0;
  const estilo = { backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, color: C.text, fontSize: 16, fontWeight: '700' as const };
  return (
    <Modal transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 12, alignSelf: 'center', width: '100%', maxWidth: 380 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>⊕ {nome}</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1, gap: 4 }}>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Dias')}</Text>
              <TextInput value={d} onChangeText={setD} keyboardType="number-pad" inputAccessoryViewButtonLabel={t('Concluído')} placeholder="0" placeholderTextColor={C.mute} style={estilo} accessibilityLabel={t('Dias')} />
            </View>
            <View style={{ flex: 1.4, gap: 4 }}>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Valor por dia')}</Text>
              <TextInput value={v} onChangeText={setV} keyboardType="decimal-pad" inputAccessoryViewButtonLabel={t('Concluído')} placeholder="0,00" placeholderTextColor={C.mute} style={estilo} accessibilityLabel={t('Valor por dia')} autoFocus />
            </View>
          </View>
          {ok && <Text style={{ color: C.sub }}>{nd} × {din(nv, moeda)} = <Text style={{ color: C.text, fontWeight: '800' }}>{din(nd * nv, moeda)}</Text></Text>}
          <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
            {!!onLimpar && <Pressable onPress={() => { onLimpar(); onFechar(); }} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Limpar')}</Text></Pressable>}
            <Pressable onPress={onFechar} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Cancelar')}</Text></Pressable>
            <Pressable disabled={!ok} onPress={() => { onOk(nd, Math.round(nv * 100) / 100); onFechar(); }} hitSlop={8}
              style={{ paddingHorizontal: 18, paddingVertical: 8, borderRadius: 10, backgroundColor: ok ? cor : C.card2 }}>
              <Text style={{ color: ok ? '#fff' : C.mute, fontWeight: '800' }}>{t('OK')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** Texto 19 (C1/C4): o ⊕ pequeno depois do nome; com dias, mostra "3 · R$ 35" e o total calculado no lugar do campo. */
function PorDia({ x, moeda, cor, pode, onAbrir }: { x: { dias?: number; porDia?: number }; moeda: string; cor: string; pode: boolean; onAbrir: () => void }) {
  if (x.dias && x.porDia) return (
    <Pressable disabled={!pode} onPress={onAbrir} hitSlop={6} accessibilityLabel={t('Dias × valor por dia')}>
      <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{x.dias} · {din(x.porDia, moeda)}</Text>
    </Pressable>);
  if (!pode) return null;
  return (
    <Pressable onPress={onAbrir} hitSlop={8} accessibilityLabel={t('Dias × valor por dia')}>
      <Text style={{ color: cor, fontSize: 15, fontWeight: '800' }}>⊕</Text>
    </Pressable>);
}

/** Texto 23 (E4): "1 por convidado × R$ 6" (ou o ⊕ para escolher). */
const qtdTxt = (q: number) => String(Math.round(q * 1000) / 1000).replace('.', ',');
function PorConvidado({ x, moeda, cor, pode, onAbrir }: { x: PorConv; moeda: string; cor: string; pode: boolean; onAbrir: () => void }) {
  if (x.qtdConv) return (
    <Pressable disabled={!pode} onPress={onAbrir} hitSlop={6} accessibilityLabel={t('Quantidade por convidado × valor unitário')}>
      <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{t('{q} por convidado', { q: qtdTxt(x.qtdConv) + (x.un ? ' ' + t(x.un) : '') })}{x.unit ? ` × ${din(x.unit, moeda)}` : ' · ' + t('falta o valor')}</Text>
    </Pressable>);
  if (!pode) return null;
  return (
    <Pressable onPress={onAbrir} hitSlop={8} accessibilityLabel={t('Quantidade por convidado × valor unitário')}>
      <Text style={{ color: cor, fontSize: 15, fontWeight: '800' }}>⊕</Text>
    </Pressable>);
}
function JanelaConvidado({ nome, x, n, moeda, cor, onOk, onLimpar, onFechar }: { nome: string; x: PorConv; n: number; moeda: string; cor: string;
  onOk: (qtd: number, unit: number) => void; onLimpar?: () => void; onFechar: () => void }) {
  const [q, setQ] = useState(x.qtdConv ? qtdTxt(x.qtdConv) : '1');
  const [v, setV] = useState(x.unit ? String(x.unit).replace('.', ',') : '');
  const nq = num(q.replace(',', '.')), nv = lerValor(v);
  const ok = nq > 0 && (!v.trim() || (isFinite(nv) && nv > 0));
  const estilo = { backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, color: C.text, fontSize: 16, fontWeight: '700' as const };
  return (
    <Modal transparent animationType="fade" onRequestClose={onFechar}>
      <Pressable onPress={onFechar} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 18, padding: 16, gap: 12, alignSelf: 'center', width: '100%', maxWidth: 380 }}>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>⊕ {nome}</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1, gap: 4 }}>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Quantidade por convidado')}{x.un ? ` (${t(x.un)})` : ''}</Text>
              <TextInput value={q} onChangeText={setQ} keyboardType="decimal-pad" inputAccessoryViewButtonLabel={t('Concluído')} placeholder="1" placeholderTextColor={C.mute} style={estilo} accessibilityLabel={t('Quantidade por convidado')} />
            </View>
            <View style={{ flex: 1.2, gap: 4 }}>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Valor unitário')}</Text>
              <TextInput value={v} onChangeText={setV} keyboardType="decimal-pad" inputAccessoryViewButtonLabel={t('Concluído')} placeholder="0,00" placeholderTextColor={C.mute} style={estilo} accessibilityLabel={t('Valor unitário')} autoFocus />
            </View>
          </View>
          {ok && nv > 0 && <Text style={{ color: C.sub }}>{n} × {qtdTxt(nq)} × {din(nv, moeda)} = <Text style={{ color: C.text, fontWeight: '800' }}>{din(Math.round(n * nq * nv * 100) / 100, moeda)}</Text></Text>}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('O total muda sozinho quando alguém confirma ou desconfirma.')}</Text>
          <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
            {!!onLimpar && <Pressable onPress={() => { onLimpar(); onFechar(); }} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Limpar')}</Text></Pressable>}
            <Pressable onPress={onFechar} hitSlop={8}><Text style={{ color: C.sub, fontWeight: '700' }}>{t('Cancelar')}</Text></Pressable>
            <Pressable disabled={!ok} onPress={() => { onOk(Math.round(nq * 1000) / 1000, isFinite(nv) && nv > 0 ? Math.round(nv * 100) / 100 : 0); onFechar(); }} hitSlop={8}
              style={{ paddingHorizontal: 18, paddingVertical: 8, borderRadius: 10, backgroundColor: ok ? cor : C.card2 }}>
              <Text style={{ color: ok ? '#fff' : C.mute, fontWeight: '800' }}>{t('OK')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** Dias da ficha para o ⊕: a viagem conta da Ida até a Volta ("Dia 1 de 7"); a parada, da chegada à saída. */
export function diasDaFicha(r: Reg): number | null {
  const ini = String(r.c.inicio || r.c.chegada || ''), fim = String(r.c.fim || r.c.saida || '');
  if (!ini || !fim || fim < ini) return null;
  const n = Math.round((Date.parse(fim + 'T12:00:00') - Date.parse(ini + 'T12:00:00')) / 864e5);
  return r.col === 'parada' ? n || 1 : n + 1;
}

/** Editor das categorias do orçamento de uma ficha (a viagem ou uma parada). */
/** Texto 23 (Eventos): o ⊕ vira "por convidado" (n = quantos convidados contam), com o valor dos fornecedores da categoria
 *  (entra sozinho como previsto) e o botão "Calcular quantidades" (Comida e Bebidas). */
export type ModoConvidados = { n: number; padrao?: () => CatOrc[]; fixos?: (c: CatOrc) => { nome: string; valor: number }[]; calcular?: (c: CatOrc) => (() => void) | null };
export function EditorOrcamento({ alvo, gastos, ctx, pode, conv }: { alvo: Reg; gastos: Reg[]; ctx: Ctx; pode: boolean; conv?: ModoConvidados }) {
  const orc = Array.isArray(alvo.c.orc) ? alvo.c.orc as CatOrc[] : conv?.padrao ? conv.padrao() : orcDe(alvo);
  const nC = conv ? conv.n : null;
  const fixosDe = (c: CatOrc) => conv?.fixos?.(c) ?? [];
  const prevDe = (c: CatOrc) => previstoCat(c, nC) + fixosDe(c).reduce((s, f) => s + f.valor, 0);
  const [linhaNova, setLinhaNova] = useState<string | null>(null);
  const [janela, setJanela] = useState<{ cat: string; linha?: string } | null>(null);   // texto 19 (C): ⊕ aberto
  const salvar = (novo: CatOrc[]) => mudarCampos(ctx.mod, alvo.id, { orc: novo });
  const mudarCat = (id: string, f: (c: CatOrc) => CatOrc | null) => salvar(orc.map(c => (c.id === id ? f(c) : c)).filter(Boolean) as CatOrc[]);
  const tirarCat = (c: CatOrc) => {
    const apagar = () => mudarCat(c.id, () => null);
    if (!previstoCat(c)) return apagar();
    Alert.alert(t('Tirar "{nome}" do orçamento?', { nome: t(c.nome) }), t('Os gastos já lançados nela continuam na lista de gastos.'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Tirar'), style: 'destructive', onPress: apagar }]);
  };
  const cor = ctx.cor;
  return (
    <View style={{ gap: 10 }}>
      {orc.map(c => {
        const prev = prevDe(c), gasto = gastoCat(gastos, c, ctx.moeda);
        const calc = conv?.calcular?.(c) ?? null;
        return (
          <View key={c.id} style={{ backgroundColor: C.card2, borderRadius: 12, padding: 10, gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 18 }}>{c.icone}</Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={{ color: C.text, fontWeight: '800', flexShrink: 1 }} numberOfLines={1}>{t(c.nome)}</Text>
                  {!c.linhas?.length && (conv
                    ? !c.qtdConv && <PorConvidado x={c} moeda={ctx.moeda} cor={cor} pode={pode} onAbrir={() => setJanela({ cat: c.id })} />
                    : <PorDia x={c} moeda={ctx.moeda} cor={cor} pode={pode} onAbrir={() => setJanela({ cat: c.id })} />)}
                </View>
                {!c.linhas?.length && !!conv && !!c.qtdConv && <PorConvidado x={c} moeda={ctx.moeda} cor={cor} pode={pode} onAbrir={() => setJanela({ cat: c.id })} />}
                {!!linhaGasto(gasto, prev, ctx.moeda) && <Text style={{ color: gasto > prev && prev ? C.danger : C.sub, fontSize: 12 }}>{linhaGasto(gasto, prev, ctx.moeda)}</Text>}
              </View>
              {c.linhas?.length || (c.dias && c.porDia) || (conv && c.qtdConv) || fixosDe(c).length
                ? <Text style={{ color: C.text, fontWeight: '800' }}>{din(prev, ctx.moeda)}</Text>
                : <Valor valor={c.valor} pode={pode} rotulo={t('Previsto para {nome}', { nome: t(c.nome) })} onSalvar={v => mudarCat(c.id, x => ({ ...x, valor: v }))} />}
              {pode && <Lixeira rotulo={t('Tirar {nome}', { nome: t(c.nome) })} onPress={() => tirarCat(c)} />}
            </View>
            <Barra gasto={gasto} previsto={prev} />
            {fixosDe(c).map((f, i) => (
              <View key={'f' + i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 26 }}>
                <Text style={{ color: C.sub, flex: 1 }} numberOfLines={1}>🤝 {f.nome}</Text>
                <Text style={{ color: C.sub, fontWeight: '700', width: 104, textAlign: 'right' }}>{din(f.valor, ctx.moeda)}</Text>
                {pode && <View style={{ width: 30 }} />}
              </View>
            ))}
            {!!calc && pode && <Pressable onPress={calc} hitSlop={6} style={{ paddingLeft: 26 }}><Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>🧮 {t('Calcular quantidades')}</Text></Pressable>}
            {c.linhas?.map(l => {
              const gl = gastoLinha(gastos, c, l, ctx.moeda), vl = valorDe(l, nC);
              return (
                <View key={l.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 26 }}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Text style={{ color: C.text, flexShrink: 1 }} numberOfLines={1}>{t(l.nome)}</Text>
                      {conv ? !l.qtdConv && <PorConvidado x={l} moeda={ctx.moeda} cor={cor} pode={pode} onAbrir={() => setJanela({ cat: c.id, linha: l.id })} />
                        : <PorDia x={l} moeda={ctx.moeda} cor={cor} pode={pode} onAbrir={() => setJanela({ cat: c.id, linha: l.id })} />}
                    </View>
                    {!!conv && !!l.qtdConv && <PorConvidado x={l} moeda={ctx.moeda} cor={cor} pode={pode} onAbrir={() => setJanela({ cat: c.id, linha: l.id })} />}
                    {!!linhaGasto(gl, vl, ctx.moeda) && <Text style={{ color: gl > vl && vl ? C.danger : C.sub, fontSize: 11 }}>{linhaGasto(gl, vl, ctx.moeda)}</Text>}
                  </View>
                  {(l.dias && l.porDia) || (conv && l.qtdConv)
                    ? <Text style={{ color: C.text, fontWeight: '800', width: 104, textAlign: 'right' }}>{din(vl, ctx.moeda)}</Text>
                    : <Valor valor={l.valor} pode={pode} rotulo={t('Previsto para {nome}', { nome: t(l.nome) })}
                    onSalvar={v => mudarCat(c.id, x => ({ ...x, linhas: (x.linhas ?? []).map(y => (y.id === l.id ? { ...y, valor: v } : y)) }))} />}
                  {pode && <Lixeira rotulo={t('Tirar {nome}', { nome: t(l.nome) })} onPress={() => mudarCat(c.id, x => ({ ...x, linhas: (x.linhas ?? []).filter(y => y.id !== l.id) }))} />}
                </View>
              );
            })}
            {pode && (linhaNova === c.id
              ? <View style={{ paddingLeft: 26 }}><NovoNome cor={cor} ph={t('Nome do item (ex.: Jantar especial)')}
                  onOk={nome => { mudarCat(c.id, x => ({ ...x, valor: x.linhas?.length ? x.valor : undefined, linhas: [...(x.linhas ?? []), { id: uid(), nome }] })); setLinhaNova(null); }} /></View>
              : <Pressable onPress={() => setLinhaNova(c.id)} hitSlop={6} style={{ paddingLeft: 26 }}>
                  <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>+ {c.linhas?.length ? t('Adicionar item') : t('Detalhar em itens')}</Text>
                </Pressable>)}
          </View>
        );
      })}
      {pode && <NovoNome cor={cor} ph={t('Nova categoria (ex.: Seguro, Presentes)')} onOk={nome => salvar([...orc, { id: uid(), nome, icone: '🏷️' }])} />}
      {!!janela && (() => {
        const c = orc.find(x => x.id === janela.cat); if (!c) return null;
        const l = janela.linha ? c.linhas?.find(y => y.id === janela.linha) : undefined;
        const x = l ?? c;
        const por = (fn: (y: LinhaOrc | CatOrc) => LinhaOrc | CatOrc) => mudarCat(c.id, cc => (l ? { ...cc, linhas: (cc.linhas ?? []).map(y => (y.id === l.id ? fn(y) as LinhaOrc : y)) } : fn(cc) as CatOrc));
        if (conv) return <JanelaConvidado nome={t(x.nome)} x={x} n={conv.n} moeda={ctx.moeda} cor={cor} onFechar={() => setJanela(null)}
          onOk={(q, v) => por(y => ({ ...y, qtdConv: q, unit: v || undefined, valor: undefined }))}
          onLimpar={x.qtdConv ? () => por(y => { const { qtdConv: _q, unit: _u, un: _n, ...resto } = y; void _q; void _u; void _n; return resto; }) : undefined} />;
        return <JanelaDias nome={t(x.nome)} dias={x.dias} porDia={x.porDia} diasPadrao={diasDaFicha(alvo)} moeda={ctx.moeda} cor={cor} onFechar={() => setJanela(null)}
          onOk={(d, v) => por(y => ({ ...y, dias: d, porDia: v, valor: Math.round(d * v * 100) / 100 }))}
          onLimpar={x.dias ? () => por(y => { const { dias: _d, porDia: _p, ...resto } = y; void _d; void _p; return resto; }) : undefined} />;
      })()}
      {pode && Array.isArray(alvo.c.orc) && (
        <Pressable onPress={() => Alert.alert(t('Voltar às categorias sugeridas?'), t('Os valores previstos desta lista serão apagados. Os gastos lançados continuam.'), [
          { text: t('Cancelar'), style: 'cancel' }, { text: t('Voltar'), style: 'destructive', onPress: () => mudarCampos(ctx.mod, alvo.id, { orc: null }) }])} hitSlop={6}>
          <Text style={{ color: C.mute, fontSize: 12 }}>↺ {t('Voltar às categorias sugeridas')}</Text>
        </Pressable>
      )}
    </View>
  );
}

/** Cartão com o total: previsto, gasto e quanto sobra (ou passou). */
export function ResumoOrcamento({ titulo, previsto, gasto, moeda, cor, outras }: { titulo: string; previsto: number; gasto: number; moeda: string; cor: string;
  /** texto 19 (D7): gastos em outras moedas (não convertidos), ex.: "€ 100,00" */
  outras?: string }) {
  const sobra = previsto - gasto;
  return (
    <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 14, padding: 12, gap: 6, borderWidth: 1, borderColor: alpha(cor, 0.35) }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>💰 {titulo}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        <View><Text style={{ color: C.sub, fontSize: 11 }}>{t('Previsto')}</Text><Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{din(previsto, moeda)}</Text></View>
        <View><Text style={{ color: C.sub, fontSize: 11 }}>{t('Gasto')}</Text><Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{din(gasto, moeda)}{outras ? ` + ${outras}` : ''}</Text></View>
        {previsto > 0 && <View><Text style={{ color: C.sub, fontSize: 11 }}>{sobra >= 0 ? t('Ainda pode gastar') : t('Passou do previsto')}</Text>
          <Text style={{ color: sobra >= 0 ? C.ok : C.danger, fontWeight: '800', fontSize: 16 }}>{din(Math.abs(sobra), moeda)}</Text></View>}
      </View>
      <Barra gasto={gasto} previsto={previsto} />
    </View>
  );
}
